import {inject, Injectable, OnDestroy} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../service/authentication.service';
import {tap} from 'rxjs/operators';
import {APP_CONFIG} from '../../../misc/inject-tokens';
import {AppConfig} from '../../../model/app-config.model';
import {supportsLocalStorage, ucsIsNil} from '../../../misc/utils';
import {ToastAlertSignalStore} from '../../../store/alert/toast-alert.signal-store';
import {TranslateService} from '@ngx-translate/core';
import {UserSignalStore} from '../../../store/user/user.signal-store';
import {PageSignalStore} from '../../../store/page/page.signal-store';

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
  userState: any;
  readonly toastAlertStore = inject(ToastAlertSignalStore);
  private readonly userSignalStore = inject(UserSignalStore);
  private readonly unsubscribe: Subject<void> = new Subject<void>();
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly pageSignalStore = inject(PageSignalStore);
  private readonly config = inject<AppConfig>(APP_CONFIG);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldBypass(req)) {
      return next.handle(req);
    }

    return this.handleAuthenticatedRequest(req, next);
  }

  /**
   * just in case the spinner state was set true directly without using the spinner service
   */
  fallbackDisableSpinner(): void {
    this.pageSignalStore.updateLoading(false);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private handleAuthenticatedRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authReq = this.addAuthorizationHeader(req);

    if (ucsIsNil(authReq)) {
      if (!this.config.production) {
        console.error(`BUG: a request fell through here unauthenticated. Variable authReq is null. This shouldn't happen.
         Rewriting with original request. Request was to url: ${req.url}`);
      }
      authReq = req;
    }

    return next.handle(authReq).pipe(
      tap(
        {next: () => {
          // intentionally left empty
        },
        error: error => this.handleErrorResponse(error)}
      )
    );
  }

  private addAuthorizationHeader(req: HttpRequest<any>): HttpRequest<any> {
    let authReq = req;
    const locale = this.userSignalStore.locale() ?? 'en';
    if (supportsLocalStorage() && this.authenticationService.isAuthenticated()) {
      const token = this.authenticationService.getToken();
      authReq = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + token).set('Accept-Language', locale),
      });
    } else {
      this.handleLocalStorageUnsupported();
    }
    return authReq;
  }

  private handleErrorResponse(error: any) {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.fallbackDisableSpinner();
      window.location.href = this.config.authenticationUrl + '/authentication/logon';
    }
  }

  private handleLocalStorageUnsupported() {
    this.toastAlertStore.danger(this.translate.instant('error.localstorage-not-supported'));
    this.router.navigateByUrl('logout');
  }

  private shouldBypass(req: HttpRequest<any>): boolean {
    const urlsToBypass = [
      this.config.salesApiUrl + '/translations',
      this.config.authenticationUrl + '/actuator/info',
      this.config.authenticationUrl + '/authentication/logon',
    ];
    return urlsToBypass.some(url => req.url?.startsWith(url)) ||
        (!req.url?.startsWith(this.config.authenticationUrl) && !req.url?.startsWith(this.config.salesApiUrl)
            && !req.url?.startsWith(this.config.salesVmsApiUrl) && !req.url?.startsWith(this.config.apiGatewayUrl));
  }
}
